/**
 * Category-driven Type Parameter Schemas for the Edit Type (Type Properties)
 * dialog. Mirrors the BIM "Type Properties" window: each element category
 * exposes its own parameter groups, and unknown parameters that live on the
 * type but are not described here fall into a dynamic "Other" group.
 *
 * Storage convention: length parameters are kept in meters inside
 * symbol.parameters (matching geometryData), and the 'length' editor shows
 * them in millimeters, converting on display and on commit.
 */

import { familyManager } from '../core/FamilyManager';

export type TypeEditorKind =
    | 'text'        // free text
    | 'number'      // unitless numeric
    | 'length'      // meters internally, mm in the UI
    | 'boolean'     // Yes / No
    | 'select'      // fixed option list
    | 'material'    // catalog material name
    | 'color'       // hex color
    | 'pattern'     // fill pattern name
    | 'familyType'; // type name reference within the same family

export interface TypeParameterDef {
    /** Key inside FamilySymbol.parameters. */
    name: string;
    /** UI label. */
    label: string;
    editor: TypeEditorKind;
    options?: string[];
    unit?: string;
    helpText?: string;
}

export interface TypeParameterGroupDef {
    name: string;
    parameters: TypeParameterDef[];
}

export interface CategoryTypeSchema {
    groups: TypeParameterGroupDef[];
    /** Composite (layered) categories get the Edit Assembly button. */
    hasAssembly?: boolean;
}

const BOOLEAN_YES_NO = ['Yes', 'No'];
const FILL_PATTERNS = [
    'None', 'Solid', 'Brick', 'Block', 'Cart', 'Insulation',
    'Crosshatch 45 deg', 'Diagonal Lines 45 deg', 'Air Space',
];
const WALL_FUNCTIONS = ['Exterior', 'Interior', 'Core (Shaft)', 'Foundation'];
const WRAP_CONDITIONS = ['Do not wrap', 'Wrap at inserts', 'Wrap at ends', 'Wrap both'];
const COARSE_FILL_COLORS = ['By category', 'Red', 'Orange', 'Yellow', 'Green', 'Cyan', 'Blue', 'Violet', 'Magenta', 'Black', 'White', 'Gray'];
const STRUCTURAL_ROLES = ['None', 'Beam', 'Brace', 'Column', 'Footing', 'Girder', 'Joist', 'Purlin', 'Stud', 'Chord'];
const JOIN_METHODS = ['Butt', 'Miter', 'Square-off'];

/** Shared identity/IFC groups appended to every category. */
function identityGroup(typeMark = true): TypeParameterGroupDef {
    return {
        name: 'Identity Data',
        parameters: [
            ...(typeMark ? [{ name: 'Type Mark', label: 'Type Mark', editor: 'text' as TypeEditorKind, helpText: 'Category code or position number for this type (e.g. W-01).' }] : []),
            { name: 'Cost', label: 'Cost', editor: 'number' as TypeEditorKind },
            { name: 'Assembly Code', label: 'Assembly Code (OmniClass)', editor: 'text' as TypeEditorKind },
            { name: 'Keynote', label: 'Keynote', editor: 'text' as TypeEditorKind },
            { name: 'Model', label: 'Model', editor: 'text' as TypeEditorKind },
            { name: 'Manufacturer', label: 'Manufacturer', editor: 'text' as TypeEditorKind },
            { name: 'Url', label: 'URL', editor: 'text' as TypeEditorKind },
            { name: 'Type Comments', label: 'Type Comments', editor: 'text' as TypeEditorKind },
            { name: 'Description', label: 'Description', editor: 'text' as TypeEditorKind },
        ],
    };
}

function ifcGroup(ifcClass: string): TypeParameterGroupDef {
    return {
        name: 'IFC Parameters',
        parameters: [
            { name: 'Export to IFC As', label: 'Export to IFC As', editor: 'text' as TypeEditorKind, helpText: `IFC class this type exports to (default: ${ifcClass}).` },
            { name: 'IFC Predefined Type', label: 'IFC Predefined Type', editor: 'text' as TypeEditorKind },
        ],
    };
}

function graphicsGroup(extra: TypeParameterDef[] = []): TypeParameterGroupDef {
    return {
        name: 'Graphics',
        parameters: [
            { name: 'Coarse Scale Fill Pattern', label: 'Coarse Scale Fill Pattern', editor: 'pattern' as TypeEditorKind, options: FILL_PATTERNS },
            { name: 'Coarse Scale Fill Color', label: 'Coarse Scale Fill Color', editor: 'select' as TypeEditorKind, options: COARSE_FILL_COLORS },
            ...extra,
        ],
    };
}

// ---------------------------------------------------------------------------
// Per-category schemas
// ---------------------------------------------------------------------------

const WALLS: CategoryTypeSchema = {
    hasAssembly: true,
    groups: [
        {
            name: 'Construction',
            parameters: [
                // Names match the keys FamilyManager seeds on symbols
                // ('function', 'wrappingAtInserts', 'thickness', 'layers').
                { name: 'function', label: 'Function', editor: 'select', options: WALL_FUNCTIONS },
                { name: 'wrappingAtInserts', label: 'Wrapping at Inserts', editor: 'select', options: WRAP_CONDITIONS },
                { name: 'wrappingAtEnds', label: 'Wrapping at Ends', editor: 'select', options: WRAP_CONDITIONS },
                { name: 'thickness', label: 'Width (Thickness)', editor: 'length' },
                { name: 'Location Line Default', label: 'Default Location Line', editor: 'select', options: ['Wall Centerline', 'Core Centerline', 'Finish Face: Exterior', 'Finish Face: Interior', 'Core Face: Exterior', 'Core Face: Interior'] },
                { name: 'Join Method', label: 'Join Method', editor: 'select', options: JOIN_METHODS },
            ],
        },
        graphicsGroup(),
        {
            name: 'Materials and Finishes',
            parameters: [
                { name: 'Structural Material', label: 'Structural Material', editor: 'material' },
                { name: 'Finish 1 Material', label: 'Finish 1 Material (Ext.)', editor: 'material' },
                { name: 'Finish 2 Material', label: 'Finish 2 Material (Int.)', editor: 'material' },
            ],
        },
        {
            name: 'Constraints',
            parameters: [
                { name: 'Default Height', label: 'Default Height', editor: 'length' },
                { name: 'Default Base Offset', label: 'Default Base Offset', editor: 'length' },
                { name: 'Default Top Offset', label: 'Default Top Offset', editor: 'length' },
            ],
        },
        {
            name: 'Analytical Model & Structural',
            parameters: [
                { name: 'Enable Analytical Model', label: 'Enable Analytical Model', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Structural Material', label: 'Analytical Structural Material', editor: 'material' },
                { name: 'Structural Usage', label: 'Structural Usage', editor: 'select', options: ['Bearing', 'Non-Bearing', 'Retaining Wall', 'Shear Wall'] },
            ],
        },
        identityGroup(),
        ifcGroup('IfcWall'),
    ],
};

const OPENING_COMMON = (kind: 'Door' | 'Window'): TypeParameterDef[] => [
    { name: 'Width', label: 'Width', editor: 'length' },
    { name: 'Height', label: 'Height', editor: 'length' },
    { name: 'Frame Width', label: `${kind === 'Door' ? 'Frame' : 'Sill'} Width`, editor: 'length' },
    { name: 'Rough Width', label: 'Rough Width', editor: 'length' },
    { name: 'Rough Height', label: 'Rough Height', editor: 'length' },
    ...(kind === 'Window'
        ? [{ name: 'Sill Height' as string, label: 'Default Sill Height', editor: 'length' as TypeEditorKind }]
        : []),
];

const DOORS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Construction',
            parameters: [
                { name: 'Door Operation', label: 'Door Operation', editor: 'select', options: ['Single-Action', 'Double-Action', 'Sliding', 'Folding', 'Swinging'] },
                { name: 'Door Style', label: 'Door Style', editor: 'select', options: ['Flush', '1 Panel', '2 Panels', '3 Panels', '1 Lite', '2 Lite', '3 Lite', '6 Lite'] },
                { name: 'Close from Both Sides', label: 'Close from Both Sides', editor: 'boolean', options: BOOLEAN_YES_NO },
            ],
        },
        {
            name: 'Dimensions',
            parameters: [
                { name: 'Width', label: 'Width', editor: 'length' },
                { name: 'Height', label: 'Height', editor: 'length' },
                { name: 'FrameWidth', label: 'Frame Width', editor: 'length' },
                // Formula-driven outputs on the seeded Single-Flush family;
                // shown read-only (greyed) in the dialog.
                { name: 'HalfWidth', label: 'Half Width', editor: 'length' },
                { name: 'RoughOpeningWidth', label: 'Rough Opening Width', editor: 'length' },
                { name: 'Rough Width', label: 'Rough Width', editor: 'length' },
                { name: 'Rough Height', label: 'Rough Height', editor: 'length' },
            ],
        },
        graphicsGroup(),
        {
            name: 'Materials and Finishes',
            parameters: [
                { name: 'Frame Material', label: 'Frame Material', editor: 'material' },
                { name: 'Panel Material', label: 'Panel Material', editor: 'material' },
                { name: 'Finish', label: 'Finish', editor: 'text' },
            ],
        },
        identityGroup(),
        ifcGroup('IfcDoor'),
    ],
};

const WINDOWS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Construction',
            parameters: [
                { name: 'Window Type', label: 'Type', editor: 'select', options: ['Fixed', 'Single-Hung', 'Double-Hung', 'Casement', 'Awning', 'Jalousie'] },
                { name: 'Interior Glazing', label: 'Interior Glazing', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Exterior Glazing', label: 'Exterior Glazing', editor: 'boolean', options: BOOLEAN_YES_NO },
            ],
        },
        {
            name: 'Dimensions',
            parameters: OPENING_COMMON('Window'),
        },
        graphicsGroup(),
        {
            name: 'Materials and Finishes',
            parameters: [
                { name: 'Frame Material', label: 'Frame Material', editor: 'material' },
                { name: 'Glass', label: 'Glass', editor: 'select', options: ['Single Pane', 'Double Pane', 'Triple Pane'] },
                { name: 'Muntins', label: 'Muntins', editor: 'select', options: ['None', 'Exterior Sund Screen', 'Interior Between Panes'] },
                { name: 'Sill Material', label: 'Sill Material', editor: 'material' },
            ],
        },
        identityGroup(),
        ifcGroup('IfcWindow'),
    ],
};

const FLOORS: CategoryTypeSchema = {
    hasAssembly: true,
    groups: [
        {
            name: 'Construction',
            parameters: [
                { name: 'Structure Description', label: 'Structure', editor: 'select', options: ['Wall Foundations', 'Slab on Grade', 'Floor Slab', 'Elevated Slab', 'Deck on Metal', 'Thickened'] },
                { name: 'Thickness', label: 'Width (Thickness)', editor: 'length' },
            ],
        },
        graphicsGroup(),
        {
            name: 'Materials and Finishes',
            parameters: [
                { name: 'Structural Material', label: 'Structural Material', editor: 'material' },
                { name: 'Underlayment Material', label: 'Underlayment Material', editor: 'material' },
                { name: 'Finish Material', label: 'Finished Floor Material', editor: 'material' },
            ],
        },
        {
            name: 'Structural',
            parameters: [
                { name: 'Structural', label: 'Structural', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Deflection', label: 'Deflection', editor: 'select', options: ['None', 'L/180', 'L/240', 'L/360', 'L/480'] },
                { name: 'Camber', label: 'Camber', editor: 'select', options: ['None', 'L/800', 'L/600', 'L/400'] },
            ],
        },
        identityGroup(),
        ifcGroup('IfcSlab'),
    ],
};

const ROOFS: CategoryTypeSchema = {
    hasAssembly: true,
    groups: [
        {
            name: 'Construction',
            parameters: [
                { name: 'Structure', label: 'Structure', editor: 'select', options: ['Warm', 'Combined', 'Cold'] },
                { name: 'Thickness', label: 'Thickness', editor: 'length' },
                { name: 'Extend Wall to Runoff', label: 'Extend Wall to Runoff', editor: 'length' },
            ],
        },
        graphicsGroup(),
        {
            name: 'Materials and Finishes',
            parameters: [
                { name: 'Roof Covering', label: 'Roof Covering', editor: 'material' },
                { name: 'Structural Material', label: 'Structural Material', editor: 'material' },
                { name: 'Thermal/Resistance Material', label: 'Thermal / Air Layer', editor: 'material' },
            ],
        },
        identityGroup(),
        ifcGroup('IfcRoof'),
    ],
};

const CEILINGS: CategoryTypeSchema = {
    hasAssembly: true,
    groups: [
        {
            name: 'Construction',
            parameters: [
                { name: 'Thickness', label: 'Thickness', editor: 'length' },
                { name: 'Height', label: 'Height Above Level', editor: 'length' },
            ],
        },
        graphicsGroup(),
        {
            name: 'Materials and Finishes',
            parameters: [
                { name: 'Finish 1 Material', label: 'Ceiling Material', editor: 'material' },
            ],
        },
        identityGroup(),
        ifcGroup('IfcCeiling'),
    ],
};

const STAIRS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Construction',
            parameters: [
                { name: 'Style', label: 'Style', editor: 'select', options: ['Assembled', 'Monolithic', 'Winder', 'Spiral'] },
                { name: 'Structure', label: 'Structure', editor: 'select', options: ['Cast-in-place', 'Precast', 'Steel Pan', 'Wood'] },
                { name: 'Runs', label: 'Number of Runs', editor: 'number' },
                { name: 'Waist Thickness', label: 'Waist Thickness', editor: 'length' },
                { name: 'Landing Above', label: 'Landing with Floor Above', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Landing Below', label: 'Landing with Floor Below', editor: 'boolean', options: BOOLEAN_YES_NO },
            ],
        },
        {
            name: 'Dimensions',
            parameters: [
                { name: 'Riser Height', label: 'Riser Height', editor: 'length' },
                { name: 'Tread Width', label: 'Actual Tread Width', editor: 'length' },
                { name: 'Nosing Length', label: 'Nosing Length', editor: 'length' },
                { name: 'Desired Actual A', label: 'Desired/Actual A', editor: 'length' },
                { name: 'Desired Actual B', label: 'Desired/Actual B', editor: 'length' },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcStair'),
    ],
};

const RAILINGS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Construction',
            parameters: [
                { name: 'Top Rail', label: 'Top Rail', editor: 'familyType' },
                { name: 'Middle Rail', label: 'Middle Rail', editor: 'familyType' },
                { name: 'Vertical Posts', label: 'Vertical Posts', editor: 'familyType' },
                { name: 'Balusters', label: 'Balusters', editor: 'familyType' },
            ],
        },
        {
            name: 'Dimensions',
            parameters: [
                { name: 'Offset', label: 'Offset', editor: 'length' },
                { name: 'Support Spacing', label: 'Support Spacing', editor: 'length' },
                { name: 'Baluster Spacing', label: 'Baluster Spacing', editor: 'length' },
                { name: 'Top Rail Offset', label: 'Top Rail Offset', editor: 'length' },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcRailing'),
    ],
};

const CURTAIN_WALLS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Construction',
            parameters: [
                { name: 'Curtain Panel', label: 'Curtain Panel', editor: 'familyType' },
                { name: 'Vertical Mullion', label: 'Vertical Mullion', editor: 'familyType' },
                { name: 'Horizontal Mullion', label: 'Horizontal Mullion', editor: 'familyType' },
                { name: 'Mullion Junction Type', label: 'Mullion Junction Type', editor: 'select', options: ['Butt', 'Cope', 'Miter'] },
                { name: 'Grid Line', label: 'Grid Line Name', editor: 'select', options: ['System Grid Line 1', 'System Grid Line 2', 'System Grid Line 3'] },
                { name: 'Grid Layout', label: 'Layout', editor: 'select', options: ['Fixed Distance', 'Fix Amount', 'Make Equal Space'] },
                { name: 'Wall Structure', label: 'Wall Structure', editor: 'select', options: ['Finish Face: Exterior', 'Finish Face: Interior', 'Core Face: Exterior', 'Core Face: Interior', 'Wall Centerline'] },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcCurtainWall'),
    ],
};

const STRUCTURAL_FRAMING: CategoryTypeSchema = {
    groups: [
        {
            name: 'Constraints',
            parameters: [
                { name: 'Z Justification', label: 'Z Justification', editor: 'select', options: ['Center', 'Top', 'Bottom', 'Left', 'Right'] },
                { name: 'Work Plane Based', label: 'Work Plane Based', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Defines Connection', label: 'Defines Connection', editor: 'boolean', options: BOOLEAN_YES_NO },
            ],
        },
        {
            name: 'Analytical Model & Structural',
            parameters: [
                { name: 'Enable Analytical Model', label: 'Enable Analytical Model', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Structural Material', label: 'Structural Material', editor: 'material' },
                { name: 'Structural Role', label: 'Structural Role', editor: 'select', options: STRUCTURAL_ROLES },
                { name: 'Section Shape', label: 'Section Shape', editor: 'select', options: ['None', 'I-Wide', 'Rectangular Hollow Section', 'Circular Hollow Section', 'Angle', 'Channel', 'Rectangular Bar', 'Round Bar'] },
                { name: 'End Release Forces', label: 'End (P) Release Forces', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Start Release Moments', label: 'Start (0) Release Moments', editor: 'boolean', options: BOOLEAN_YES_NO },
            ],
        },
        {
            name: 'Dimensions',
            parameters: [
                { name: 'Family and Type Cut Offset', label: 'Family and Type Cut Offset', editor: 'length' },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcBeam'),
    ],
};

const STRUCTURAL_COLUMNS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Dimensions',
            parameters: [
                { name: 'Depth', label: 'Depth', editor: 'length' },
                { name: 'Width', label: 'Width', editor: 'length' },
            ],
        },
        {
            name: 'Constraints',
            parameters: [
                { name: 'z Justification', label: 'z Justification', editor: 'select', options: ['Top', 'Bottom', 'Center'] },
                { name: 'Defines Connection', label: 'Defines Connection', editor: 'boolean', options: BOOLEAN_YES_NO },
            ],
        },
        {
            name: 'Analytical Model & Structural',
            parameters: [
                { name: 'Enable Analytical Model', label: 'Enable Analytical Model', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Structural Material', label: 'Structural Material', editor: 'material' },
                { name: 'Section Shape', label: 'Section Shape', editor: 'select', options: ['None', 'Round', 'Pipe - Round', 'I-Wide', 'Rectangular Hollow Section', 'W-Wide', 'Square Bar', 'Rectangular Bar'] },
                { name: 'Is Loaded on Braced Frame', label: 'Is Loaded on Braced Frame', editor: 'boolean', options: BOOLEAN_YES_NO },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcColumn'),
    ],
};

const PHOTOMETRICS_PARAMS: TypeParameterDef[] = [
    { name: 'Initial Intensity', label: 'Initial Intensity', editor: 'number', unit: 'lm' },
    { name: 'Initial Luminous Color', label: 'Initial Luminous Color', editor: 'color' },
    { name: 'Color Temperature', label: 'Color Temperature', editor: 'number', unit: 'K' },
    { name: 'Ballast Lamp Lumens Heat', label: 'Ballast Lamp Lumens Heat', editor: 'number' },
    { name: 'Photometric Web File', label: 'Photometric Web File', editor: 'text', helpText: 'Path to .ies photometric data file.' },
    { name: 'Light Loss Factor', label: 'Light Loss Factor', editor: 'number' },
];

const LIGHTING_FIXTURES: CategoryTypeSchema = {
    groups: [
        {
            name: 'Construction',
            parameters: [
                { name: 'Mounting Type', label: 'Mounting Type', editor: 'select', options: ['Recessed', 'Surface', 'Sofft', 'Suspended'] },
                { name: 'Distribution', label: 'Distribution', editor: 'select', options: ['Symmetric', 'Asymmetric', 'Diffuse'] },
            ],
        },
        {
            name: 'Photometrics',
            parameters: PHOTOMETRICS_PARAMS,
        },
        {
            name: 'Electrical',
            parameters: [
                { name: 'Voltage', label: 'Voltage', editor: 'number', unit: 'V' },
                { name: 'Wattage', label: 'Wattage', editor: 'number', unit: 'W' },
                { name: 'Apparent Load', label: 'Apparent Load', editor: 'number', unit: 'VA' },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcLighting'),
    ],
};

const PIPES: CategoryTypeSchema = {
    groups: [
        {
            name: 'Piping System',
            parameters: [
                { name: 'System Classification', label: 'System Classification', editor: 'select', options: ['Undefined', 'Domestic Cold Water', 'Domestic Hot Water', 'Sanitary Waste', 'Vent', 'Fire Protection', 'Hydronic Return', 'Hydronic Supply', 'Natural Gas', 'Steam'] },
                { name: 'Fluid', label: 'Fluid', editor: 'select', options: ['Undefined', 'Cold Water', 'Hot Water', 'Waste', 'Air', 'Gas'] },
                { name: 'Pipe Material', label: 'Pipe Material', editor: 'material' },
                { name: 'Outer Diameter', label: 'Outer Diameter', editor: 'length' },
                { name: 'Wall Thickness', label: 'Wall Thickness', editor: 'length' },
                { name: 'Insulation Thickness', label: 'Insulation Thickness', editor: 'length' },
            ],
        },
        {
            name: 'Analysis',
            parameters: [
                { name: 'Friction Factor', label: 'Friction Factor', editor: 'number' },
                { name: 'Roughness', label: 'Roughness', editor: 'number' },
                { name: 'Pressure Drop', label: 'Pressure Drop', editor: 'number', unit: 'Pa' },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcPipeSegment'),
    ],
};

const DUCTS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Duct',
            parameters: [
                { name: 'System Classification', label: 'System Classification', editor: 'select', options: ['Undefined', 'Return Air', 'Supply Air', 'Exhaust Air', 'Recirculation'] },
                { name: 'Duct Material', label: 'Duct Material', editor: 'material' },
                { name: 'Shape', label: 'Shape', editor: 'select', options: ['Flat Oval', 'Round', 'Rectangular'] },
                { name: 'Duct Width', label: 'Width', editor: 'length' },
                { name: 'Duct Height', label: 'Height', editor: 'length' },
                { name: 'Duct Insulation Thickness', label: 'Insulation Thickness', editor: 'length' },
                { name: 'Pressure Class', label: 'Pressure Class', editor: 'select', options: ['Low', 'Medium', 'High'] },
            ],
        },
        {
            name: 'Analysis',
            parameters: [
                { name: 'Friction', label: 'Unit Friction', editor: 'number' },
                { name: 'Pressure Drop', label: 'Pressure Drop', editor: 'number', unit: 'Pa' },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcDuctSegment'),
    ],
};

const ELECTRICAL_PARAMS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Electrical',
            parameters: [
                { name: 'Voltage', label: 'Voltage', editor: 'number', unit: 'V' },
                { name: 'Apparent Load', label: 'Apparent Load', editor: 'number', unit: 'VA' },
                { name: 'Active Load', label: 'Active Load', editor: 'number', unit: 'W' },
                { name: 'Number of Poles', label: 'Number of Poles', editor: 'number' },
                { name: 'Phase', label: 'Phase', editor: 'select', options: ['1', '3'] },
                { name: 'Frequency', label: 'Frequency', editor: 'number', unit: 'Hz' },
                { name: 'Circuit Number', label: 'Circuit Number', editor: 'text' },
            ],
        },
        {
            name: 'Construction',
            parameters: [
                { name: 'Enclosure Rating', label: 'Enclosure Rating', editor: 'select', options: ['Undefined', 'NEMA 1', 'NEMA 3R', 'NEMA 4', 'NEMA 12'] },
                { name: 'Material', label: 'Material', editor: 'material' },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcElectricGenerator'),
    ],
};

const TEXT_NOTES: CategoryTypeSchema = {
    groups: [
        {
            name: 'Text',
            parameters: [
                { name: 'Text Font', label: 'Text Font', editor: 'select', options: ['Arial', 'Tahoma', 'Times New Roman', 'Courier New', 'Verdana'] },
                { name: 'Font Size', label: 'Font Size', editor: 'number', unit: 'mm' },
                { name: 'Bold', label: 'Bold', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Italic', label: 'Italic', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Underline', label: 'Underline', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Text Color', label: 'Color', editor: 'color' },
                { name: 'Background', label: 'Background', editor: 'select', options: ['Transparent', 'Opaque'] },
            ],
        },
        {
            name: 'Leader',
            parameters: [
                { name: 'Leader', label: 'Leader', editor: 'select', options: ['None', 'Arrowheads', 'Straight Leader'] },
                { name: 'Leader Arrowhead', label: 'Arrowhead Type', editor: 'familyType' },
                { name: 'Text Weight', label: 'Text Weight', editor: 'select', options: ['Thin Lines', 'Half Width', 'Thick Lines', 'Double Width'] },
            ],
        },
        {
            name: 'Graphics',
            parameters: [
                { name: 'Annotation Scale', label: 'Annotation Size', editor: 'select', options: ['Sheet Title', '1/32 in', '1/16 in', '1/8 in', '3/16 in', '1/4 in'] },
            ],
        },
        identityGroup(false),
    ],
};

const DIMENSIONS_ANNOTATION: CategoryTypeSchema = {
    groups: [
        {
            name: 'Text',
            parameters: [
                { name: 'Text Font', label: 'Text Font', editor: 'select', options: ['Arial', 'Tahoma', 'Times New Roman', 'Courier New', 'Verdana'] },
                { name: 'Font Size', label: 'Font Size', editor: 'number', unit: 'mm' },
                { name: 'Bold', label: 'Bold', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Italic', label: 'Italic', editor: 'boolean', options: BOOLEAN_YES_NO },
            ],
        },
        {
            name: 'Marks',
            parameters: [
                { name: 'Arrowhead Type', label: 'Arrowhead', editor: 'familyType' },
                { name: 'Arrowhead Size', label: 'Arrowhead Size', editor: 'number', unit: 'mm' },
                { name: 'Tick Style', label: 'Tick', editor: 'select', options: ['None', 'Tick 1/16 in', 'Circle', 'Slash', 'Dot'] },
                { name: 'Tick Size', label: 'Tick Size', editor: 'number', unit: 'mm' },
                { name: 'Linear Dim Weight', label: 'Linear Dim Weight', editor: 'select', options: ['Thin Lines', 'Half Width', 'Thick Lines', 'Double Width'] },
            ],
        },
        {
            name: 'Extension',
            parameters: [
                { name: 'Extension Snap', label: 'Extension Snap', editor: 'boolean', options: BOOLEAN_YES_NO },
                { name: 'Extension Offset', label: 'Extension Offset', editor: 'number', unit: 'mm' },
            ],
        },
        identityGroup(false),
    ],
};

/** Fallback for categories without a dedicated schema. */
const GENERIC: CategoryTypeSchema = {
    groups: [
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcProduct'),
    ],
};

// ==================== Steel Fabrication Categories ====================

const STEEL_PLATES: CategoryTypeSchema = {
    groups: [
        {
            name: 'Dimensions',
            parameters: [
                { name: 'Thickness', label: 'Thickness', editor: 'number', unit: 'mm' },
                { name: 'Elevation', label: 'Elevation', editor: 'number', unit: 'm' },
            ],
        },
        {
            name: 'Identity Data',
            parameters: [
                { name: 'Material', label: 'Material', editor: 'material' },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcPlate'),
    ],
};

const STEEL_BOLTS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Fastening',
            parameters: [
                { name: 'Bolt Standard', label: 'Standard', editor: 'select', options: ['A325', 'A490', 'A194', 'Metric 8.8', 'Metric 10.9'] },
                { name: 'Bolt Diameter', label: 'Diameter', editor: 'number', unit: 'mm' },
                { name: 'Hole Type', label: 'Hole Type', editor: 'select', options: ['Standard', 'Oversized', 'Slotted', 'Countersunk'] },
            ],
        },
        {
            name: 'Layout',
            parameters: [
                { name: 'Rows', label: 'Rows', editor: 'number' },
                { name: 'Columns', label: 'Columns', editor: 'number' },
                { name: 'Spacing', label: 'Spacing', editor: 'number', unit: 'mm' },
                { name: 'Quantity', label: 'Quantity', editor: 'number' },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcMechanicalFastener'),
    ],
};

const STEEL_WELDS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Weld',
            parameters: [
                { name: 'Weld Type', label: 'Type', editor: 'select', options: ['Shop', 'Site'] },
                { name: 'Weld Symbol', label: 'Symbol', editor: 'select', options: ['Fillet', 'Groove', 'Plug', 'Slot'] },
                { name: 'Weld Size', label: 'Size', editor: 'number', unit: 'mm' },
                { name: 'Weld Length', label: 'Length', editor: 'number', unit: 'm' },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcWeldingElement'),
    ],
};

// ==================== Precast Fabrication Categories ====================

const PRECAST_WALLS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Panel',
            parameters: [
                { name: 'Mark', label: 'Panel Mark', editor: 'text' },
                { name: 'Thickness', label: 'Thickness', editor: 'number', unit: 'mm' },
                { name: 'Height', label: 'Height', editor: 'number', unit: 'mm' },
                { name: 'Joint Width', label: 'Joint Width', editor: 'number', unit: 'mm' },
            ],
        },
        {
            name: 'Production',
            parameters: [
                { name: 'Manufacturing Status', label: 'Status', editor: 'select', options: ['To Fabricate', 'In Production', 'Shipped', 'Erected'] },
                { name: 'Concrete Strength', label: 'Concrete Strength', editor: 'select', options: ['C25/30', 'C30/37', 'C35/45', 'C40/50'] },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcWall'),
    ],
};

const PRECAST_FLOORS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Panel',
            parameters: [
                { name: 'Mark', label: 'Panel Mark', editor: 'text' },
                { name: 'Thickness', label: 'Thickness', editor: 'number', unit: 'mm' },
                { name: 'Floor System Type', label: 'System', editor: 'select', options: ['Hollow Core', 'Solid Slab', 'Double Tee'] },
                { name: 'Joint Width', label: 'Joint Width', editor: 'number', unit: 'mm' },
            ],
        },
        {
            name: 'Production',
            parameters: [
                { name: 'Manufacturing Status', label: 'Status', editor: 'select', options: ['To Fabricate', 'In Production', 'Shipped', 'Erected'] },
                { name: 'Concrete Strength', label: 'Concrete Strength', editor: 'select', options: ['C25/30', 'C30/37', 'C35/45', 'C40/50'] },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcSlab'),
    ],
};

const PRECAST_COLUMNS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Member',
            parameters: [
                { name: 'Mark', label: 'Member Mark', editor: 'text' },
                { name: 'Width', label: 'Width', editor: 'number', unit: 'mm' },
                { name: 'Depth', label: 'Depth', editor: 'number', unit: 'mm' },
            ],
        },
        {
            name: 'Production',
            parameters: [
                { name: 'Manufacturing Status', label: 'Status', editor: 'select', options: ['To Fabricate', 'In Production', 'Shipped', 'Erected'] },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcColumn'),
    ],
};

const PRECAST_BEAMS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Member',
            parameters: [
                { name: 'Mark', label: 'Member Mark', editor: 'text' },
                { name: 'Width', label: 'Width', editor: 'number', unit: 'mm' },
                { name: 'Depth', label: 'Depth', editor: 'number', unit: 'mm' },
            ],
        },
        {
            name: 'Production',
            parameters: [
                { name: 'Manufacturing Status', label: 'Status', editor: 'select', options: ['To Fabricate', 'In Production', 'Shipped', 'Erected'] },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcBeam'),
    ],
};

const PRECAST_MOUNTING_PARTS: CategoryTypeSchema = {
    groups: [
        {
            name: 'Part',
            parameters: [
                { name: 'Part Type', label: 'Type', editor: 'select', options: ['Lifting Anchor', 'Handling Loop', 'Insert Plate', 'Bolt Anchor', 'Threaded Insert', 'Shear Key'] },
                { name: 'Nominal Size', label: 'Nominal Size', editor: 'text' },
                { name: 'Host Element Id', label: 'Host', editor: 'text' },
            ],
        },
        graphicsGroup(),
        identityGroup(),
        ifcGroup('IfcEmbeddedSetting'),
    ],
};

const PRECAST_REINFORCEMENT: CategoryTypeSchema = {
    groups: [
        {
            name: 'Reinforcement',
            parameters: [
                { name: 'Wire Diameter', label: 'Ø (mm)', editor: 'number', unit: 'mm' },
                { name: 'Spacing', label: 'Spacing', editor: 'number', unit: 'mm' },
                { name: 'Host Category', label: 'Host Category', editor: 'text' },
            ],
        },
        graphicsGroup(),
        identityGroup(false),
        ifcGroup('IfcReinforcingMesh'),
    ],
};

const SCHEMAS: Record<string, CategoryTypeSchema> = {
    'Walls': WALLS,
    'Doors': DOORS,
    'Windows': WINDOWS,
    'Floors': FLOORS,
    'Roofs': ROOFS,
    'Ceilings': CEILINGS,
    'Stairs': STAIRS,
    'Railings': RAILINGS,
    'Curtain Walls': CURTAIN_WALLS,
    'Structural Framing': STRUCTURAL_FRAMING,
    'Structural Columns': STRUCTURAL_COLUMNS,
    'Steel Plates': STEEL_PLATES,
    'Steel Bolts': STEEL_BOLTS,
    'Steel Welds': STEEL_WELDS,
    'Lighting Fixtures': LIGHTING_FIXTURES,
    'Pipes': PIPES,
    'Flex Pipes': PIPES,
    'Pipe Placeholders': PIPES,
    'Ducts': DUCTS,
    'Flex Ducts': DUCTS,
    'Air Terminals': DUCTS,
    'Mechanical Equipment': ELECTRICAL_PARAMS,
    'Electrical Equipment': ELECTRICAL_PARAMS,
    'Wires': ELECTRICAL_PARAMS,
    'Cable Trays': ELECTRICAL_PARAMS,
    'Conduits': ELECTRICAL_PARAMS,
    'Text Notes': TEXT_NOTES,
    'Notes': TEXT_NOTES,
    'Tags': DIMENSIONS_ANNOTATION,
    'Dimensions': DIMENSIONS_ANNOTATION,
    // Precast tab categories
    'Precast Walls': PRECAST_WALLS,
    'Precast Floors': PRECAST_FLOORS,
    'Precast Columns': PRECAST_COLUMNS,
    'Precast Beams': PRECAST_BEAMS,
    'Precast Mounting Parts': PRECAST_MOUNTING_PARTS,
    'Precast Reinforcement': PRECAST_REINFORCEMENT,
};

/** Ordered parameter-group schema for a category. */
export function getTypeSchema(category: string): CategoryTypeSchema {
    return SCHEMAS[category] ?? GENERIC;
}

/** Flat lookup of every described parameter for a category. */
export function getTypeParameterDefs(category: string): Map<string, TypeParameterDef> {
    const map = new Map<string, TypeParameterDef>();
    getTypeSchema(category).groups.forEach(group => {
        group.parameters.forEach(p => map.set(p.name, p));
    });
    return map;
}

/**
 * Split a type's raw parameter bag into schema-described rows plus an "Other"
 * tail for unknown keys (BIM lists unclassified values at the bottom).
 * Values that are not primitives (layers, nested objects) are skipped — they
 * belong to the Edit Assembly screen, not the table.
 */
export function resolveTypeParameterRows(
    category: string,
    parameters: Record<string, any>,
): { group: string; defs: TypeParameterDef[]; values: Record<string, any> }[] {
    const schema = getTypeSchema(category);
    const consumed = new Set<string>();
    const rows = schema.groups.map(group => {
        const present: TypeParameterDef[] = [];
        const values: Record<string, any> = {};
        group.parameters.forEach(def => {
            consumed.add(def.name);
            present.push(def);
            values[def.name] = parameters[def.name] ?? defaultForEditor(def);
        });
        return { group: group.name, defs: present, values };
    });

    // Other: leftover primitive parameters not covered by the schema.
    const otherDefs: TypeParameterDef[] = [];
    const otherValues: Record<string, any> = {};
    Object.entries(parameters).forEach(([key, value]) => {
        if (consumed.has(key)) return;
        if (value === null || value === undefined || typeof value === 'object') return;
        otherDefs.push({ name: key, label: key, editor: typeof value === 'boolean' ? 'boolean' : typeof value === 'number' ? 'number' : 'text' });
        otherValues[key] = value;
    });
    if (otherDefs.length > 0) {
        rows.push({ group: 'Other', defs: otherDefs, values: otherValues });
    }
    return rows;
}

function defaultForEditor(def: TypeParameterDef): any {
    switch (def.editor) {
        case 'boolean': return 'No';
        case 'length': return 0;
        case 'number': return 0;
        case 'color': return '#000000';
        default: return '';
    }
}

// ---------------------------------------------------------------------------
// Compound structure (Edit Assembly) helpers
// ---------------------------------------------------------------------------

export interface AssemblyLayerRow {
    /** 'Structure' | 'Substrate' | 'Thermal/Air' | 'Finish 1' | 'Finish 2' | 'Membrane' | 'Wrapping' */
    function: string;
    materialId: string;
    /** meters */
    thickness: number;
    wraps: boolean;
}

export interface TypeAssembly {
    layerStation: string; // 'Finish 1 [Face Exterior]' | 'Core Boundary [Center]' etc.
    structure: string;
    layers: AssemblyLayerRow[];
}

export const ASSEMBLY_FUNCTIONS = ['Structure', 'Substrate', 'Thermal/Air', 'Finish 1', 'Finish 2', 'Membrane', 'Membrane 2'];

/** Read the stored assembly of a type (falls back to a single-layer default). */
export function readTypeAssembly(familyName: string, typeName: string): TypeAssembly | null {
    const symbol = familyManager.getType(familyName, typeName);
    if (!symbol) return null;
    const raw = symbol.parameters['layers'] ?? symbol.parameters['Layers'];
    const layers: AssemblyLayerRow[] = Array.isArray(raw) && raw.length > 0
        ? raw.map((l: any) => ({
            function: String(l.function ?? 'Structure'),
            materialId: String(l.materialId ?? l.material ?? 'Generic'),
            thickness: typeof l.thickness === 'number' ? l.thickness : 0.2,
            wraps: Boolean(l.wraps),
        }))
        : [{ function: 'Structure', materialId: 'Generic', thickness: typeof symbol.parameters['thickness'] === 'number' ? symbol.parameters['thickness'] : 0.2, wraps: false }];

    return {
        layerStation: symbol.parameters['layerStation'] ?? 'Finish 1 [Face Exterior]',
        structure: symbol.parameters['function'] ?? 'Exterior',
        layers,
    };
}

/** Write an assembly back to a type and mirror its total into 'thickness'. */
export function writeTypeAssembly(
    familyName: string,
    typeName: string,
    assembly: TypeAssembly,
): Record<string, any> | null {
    const symbol = familyManager.getType(familyName, typeName);
    if (!symbol) return null;
    const total = assembly.layers.reduce((sum, l) => sum + (l.thickness || 0), 0);
    const patch: Record<string, any> = {
        layers: assembly.layers.map(l => ({
            function: l.function,
            materialId: l.materialId,
            thickness: l.thickness,
            wraps: l.wraps,
        })),
        thickness: total,
        layerStation: assembly.layerStation,
        function: assembly.structure,
    };
    familyManager.updateTypeParameters(familyName, typeName, patch);
    return patch;
}
